// src/components/Layout/Footer.jsx
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import logo from "../../assets/images/cac-logo.jpeg";

const API_URL = process.env.REACT_APP_API_URL || "https://chineafriquecargo.com";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    fetchWhatsappNumbers();

    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchWhatsappNumbers = async () => {
    try {
      // ✅ URL relative au lieu d'URL absolue
      const response = await axios.get(`${API_URL}/whatsapp/public/`);
      let numbers = [];
      if (response.data && response.data.results) {
        numbers = response.data.results;
      } else if (Array.isArray(response.data)) {
        numbers = response.data;
      }
      if (numbers.length === 0) {
        setWhatsappNumbers([
          { phone_number: "+237 612345678", is_default: true },
        ]);
      } else {
        setWhatsappNumbers(numbers);
      }
    } catch (err) {
      console.error("Erreur chargement numéros WhatsApp:", err);
      setWhatsappNumbers([
        { phone_number: "+237 612345678", is_default: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Gestion des valeurs null/undefined
  const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return String(phone)
      .replace(/\s+/g, "")
      .replace(/[^0-9+]/g, "");
  };

  const defaultNumber =
    whatsappNumbers.find((n) => n.is_default) || whatsappNumbers[0];
  const mainWhatsapp = defaultNumber?.phone_number || "+237 612345678";

  const handleLogoError = () => {
    setLogoError(true);
  };

  // ✅ Skeleton loader amélioré
  if (loading) {
    return (
      <footer
        className="footer-skeleton"
        role="status"
        aria-label="Chargement du footer"
      >
        <Container>
          <div className="text-center py-5">
            <div className="skeleton-spinner" aria-hidden="true"></div>
            <p className="mt-3 text-white-50">Chargement...</p>
          </div>
        </Container>
        <style>{`
          .footer-skeleton {
            background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 50%, #0a0a2a 100%);
            margin-top: auto;
            min-height: 200px;
          }
          .skeleton-spinner {
            width: 40px;
            height: 40px;
            margin: 0 auto;
            border: 3px solid rgba(255,255,255,0.1);
            border-top-color: #F9A825;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </footer>
    );
  }

  return (
    <footer
      className={`footer-modern ${scrolled ? "footer-visible" : ""}`}
      role="contentinfo"
      aria-label="Pied de page"
    >
      {/* Vagues décoratives */}
      <div className="footer-waves" aria-hidden="true">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z"
            fill="#f8f9fa"
          />
        </svg>
      </div>

      <div className="footer-content">
        <Container>
          <Row className="footer-grid">
            {/* Logo & Description */}
            <Col lg={4} md={6} className="footer-col">
              <div className="footer-brand">
                <div className="brand-logo">
                  {!logoError ? (
                    <Image
                      src={logo}
                      alt="CAC - Chine Afrique Cargo Logo"
                      className="brand-image"
                      onError={handleLogoError}
                      loading="lazy"
                      width="55"
                      height="55"
                    />
                  ) : (
                    <div className="brand-icon-fallback" aria-hidden="true">
                      <span>🚚</span>
                    </div>
                  )}
                </div>
                <div className="brand-info">
                  <h3 className="brand-name">
                    <span className="text-red">CAC</span>{" "}
                    <span className="text-gold">Chine Afrique Cargo</span>
                  </h3>
                  <p className="brand-desc">
                    Transport de marchandises entre la Chine et l'Afrique.
                    Solution logistique fiable et rapide pour vos
                    importations/exportations.
                  </p>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="social-section">
                <a
                  href={`https://wa.me/${cleanPhoneNumber(mainWhatsapp)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                  aria-label="Nous contacter sur WhatsApp"
                >
                  <span className="btn-icon" aria-hidden="true">
                    💬
                  </span>
                  WhatsApp: {mainWhatsapp}
                </a>
                {whatsappNumbers.length > 1 && (
                  <div className="other-numbers">
                    <small>Autres numéros :</small>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {whatsappNumbers
                        .filter((n) => !n.is_default)
                        .map((num, idx) => (
                          <a
                            key={idx}
                            href={`https://wa.me/${cleanPhoneNumber(num.phone_number)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="other-number"
                            aria-label={`WhatsApp ${num.phone_number}`}
                          >
                            📱 {num.phone_number}
                          </a>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </Col>

            {/* Liens utiles */}
            <Col lg={2} md={6} className="footer-col">
              <h5 className="footer-title">Liens utiles</h5>
              <ul className="footer-links" aria-label="Liens utiles">
                <li>
                  <Link to="/">
                    <span aria-hidden="true">→</span> Accueil
                  </Link>
                </li>
                <li>
                  <Link to="/services">
                    <span aria-hidden="true">→</span> Services
                  </Link>
                </li>
                <li>
                  <Link to="/about">
                    <span aria-hidden="true">→</span> À propos
                  </Link>
                </li>
                <li>
                  <Link to="/tracking">
                    <span aria-hidden="true">→</span> Suivi colis
                  </Link>
                </li>
                <li>
                  <Link to="/store">
                    <span aria-hidden="true">→</span> CAC Store
                  </Link>
                </li>
              </ul>
            </Col>

            {/* Support */}
            <Col lg={2} md={6} className="footer-col">
              <h5 className="footer-title">Support</h5>
              <ul className="footer-links" aria-label="Support">
                <li>
                  <Link to="/contact">
                    <span aria-hidden="true">→</span> Contact
                  </Link>
                </li>
                <li>
                  <Link to="/faq">
                    <span aria-hidden="true">→</span> FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/cgv">
                    <span aria-hidden="true">→</span> CGV
                  </Link>
                </li>
                <li>
                  <Link to="/mentions-legales">
                    <span aria-hidden="true">→</span> Mentions légales
                  </Link>
                </li>
                <li>
                  <Link to="/confidentialite">
                    <span aria-hidden="true">→</span> Confidentialité
                  </Link>
                </li>
              </ul>
            </Col>

            {/* Contact */}
            <Col lg={4} md={6} className="footer-col">
              <h5 className="footer-title">Contact</h5>
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-icon" aria-hidden="true">
                    📞
                  </span>
                  <div className="contact-text">
                    <span className="contact-label">WhatsApp</span>
                    <span className="contact-value">{mainWhatsapp}</span>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon" aria-hidden="true">
                    📧
                  </span>
                  <div className="contact-text">
                    <span className="contact-label">Email</span>
                    <a
                      href="mailto:contact@chineafriquecargo.com"
                      className="contact-value"
                    >
                      contact@chineafriquecargo.com
                    </a>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon" aria-hidden="true">
                    📍
                  </span>
                  <div className="contact-text">
                    <span className="contact-label">Adresse</span>
                    <span className="contact-value">
                      佛山市南海区里水镇湖州村湖州大道10号
                    </span>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon" aria-hidden="true">
                    ⏰
                  </span>
                  <div className="contact-text">
                    <span className="contact-label">Horaires</span>
                    <span className="contact-value">
                      Lun-Sam: 9:30-20:00 | Dim: 9:30-18:00
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <div className="footer-divider" aria-hidden="true"></div>
            <div className="footer-copyright">
              <p>
                &copy; {currentYear} CAC - Chine Afrique Cargo. Tous droits
                réservés.
              </p>
              <div className="footer-legal">
                <Link to="/mentions-legales">Mentions légales</Link>
                <span className="separator" aria-hidden="true">
                  |
                </span>
                <Link to="/confidentialite">Confidentialité</Link>
                <span className="separator" aria-hidden="true">
                  |
                </span>
                <Link to="/cgv">CGV</Link>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <style>{`
        .footer-modern {
          background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 50%, #0a0a2a 100%);
          margin-top: auto;
          position: relative;
          overflow: hidden;
        }
        
        .footer-waves {
          position: absolute;
          top: -1px;
          left: 0;
          right: 0;
          opacity: 0.1;
        }
        
        .footer-content {
          position: relative;
          z-index: 2;
          padding: 60px 0 30px;
        }
        
        .footer-grid {
          gap: 40px 0;
        }
        
        .footer-col {
          animation: fadeInUp 0.6s ease forwards;
          opacity: 0;
        }
        
        .footer-col:nth-child(1) { animation-delay: 0.1s; }
        .footer-col:nth-child(2) { animation-delay: 0.2s; }
        .footer-col:nth-child(3) { animation-delay: 0.3s; }
        .footer-col:nth-child(4) { animation-delay: 0.4s; }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Brand Section */
        .footer-brand {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .brand-logo {
          flex-shrink: 0;
        }
        
        .brand-image {
          width: 55px;
          height: 55px;
          object-fit: contain;
          border-radius: 14px;
          transition: transform 0.3s ease;
        }
        
        .footer-brand:hover .brand-image {
          transform: rotate(3deg) scale(1.05);
        }
        
        .brand-icon-fallback {
          width: 55px;
          height: 55px;
          background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          transition: transform 0.3s ease;
        }
        
        .footer-brand:hover .brand-icon-fallback {
          transform: rotate(5deg);
        }
        
        .brand-name {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        
        .text-red {
          color: #C62828;
        }
        
        .text-gold {
          color: #F9A825;
        }
        
        .brand-desc {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
          margin: 0;
        }
        
        /* WhatsApp Button */
        .social-section {
          margin-top: 20px;
        }
        
        .whatsapp-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #25D366;
          padding: 10px 20px;
          border-radius: 50px;
          color: white;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
        }
        
        .whatsapp-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.4);
          color: white;
        }
        
        .other-numbers {
          margin-top: 12px;
        }
        
        .other-numbers small {
          display: block;
          color: rgba(255, 255, 255, 0.5);
          font-size: 11px;
          margin-bottom: 6px;
        }
        
        .other-number {
          display: inline-block;
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 12px;
          margin-right: 12px;
          margin-bottom: 6px;
          transition: all 0.3s ease;
        }
        
        .other-number:hover {
          color: #F9A825;
          transform: translateX(3px);
        }
        
        /* Footer Title */
        .footer-title {
          color: #F9A825;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
          position: relative;
          display: inline-block;
        }
        
        .footer-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0;
          width: 40px;
          height: 3px;
          background: linear-gradient(90deg, #C62828, #F9A825);
          border-radius: 3px;
        }
        
        /* Footer Links */
        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .footer-links li {
          margin-bottom: 12px;
        }
        
        .footer-links a {
          color: rgba(255, 255, 255, 0.6);
          text-decoration: none;
          font-size: 13px;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        
        .footer-links a span {
          opacity: 0;
          transform: translateX(-5px);
          transition: all 0.3s ease;
        }
        
        .footer-links a:hover {
          color: #F9A825;
          transform: translateX(5px);
        }
        
        .footer-links a:hover span {
          opacity: 1;
          transform: translateX(0);
        }
        
        /* Contact Items */
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        
        .contact-icon {
          font-size: 20px;
          min-width: 28px;
        }
        
        .contact-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .contact-label {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .contact-value {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
        }
        
        .contact-value a {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
        }
        
        .contact-value a:hover {
          color: #F9A825;
        }
        
        /* Footer Bottom */
        .footer-bottom {
          margin-top: 40px;
        }
        
        .footer-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          margin-bottom: 20px;
        }
        
        .footer-copyright {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        
        .footer-copyright p {
          color: rgba(255, 255, 255, 0.4);
          font-size: 12px;
          margin: 0;
        }
        
        .footer-legal {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .footer-legal a {
          color: rgba(255, 255, 255, 0.4);
          text-decoration: none;
          font-size: 12px;
          transition: all 0.3s ease;
        }
        
        .footer-legal a:hover {
          color: #F9A825;
        }
        
        .separator {
          color: rgba(255, 255, 255, 0.2);
        }
        
        /* Responsive */
        @media (max-width: 992px) {
          .footer-content {
            padding: 40px 0 20px;
          }
          
          .footer-copyright {
            flex-direction: column;
            text-align: center;
          }
          
          .footer-legal {
            justify-content: center;
          }
        }
        
        @media (max-width: 768px) {
          .footer-brand {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          
          .brand-name {
            font-size: 18px;
          }
          
          .brand-image, .brand-icon-fallback {
            width: 48px;
            height: 48px;
          }
          
          .footer-title {
            font-size: 16px;
          }
          
          .footer-title::after {
            width: 30px;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
